import type { SetStoreFunction } from "solid-js/store";
import { createStore } from "solid-js/store";
import type { ContextProviderComponent } from "solid-js";
import { createContext, untrack } from "solid-js";
import { initializedContext } from "../utils/initializedUtil";
import type { Scope, ThemeId } from "../types";
import type FieldId from "../fields/FieldId";
import type RuleId from "../fields/RuleId";
import type SubformId from "./SubformId";
import type { FieldConfig, VariableConfig } from "../fields/types";
import type { Integration } from "../integrations/types";
import type { StringTemplate } from "../utils/stringTemplateUtil";
import type VariableId from "../variables/VariableId";
import type FormId from "./FormId";
import type FormVersionId from "./FormVersionId";

export type FormVersion = {
  id: FormVersionId;
  formId: FormId;
  meta: FormVersionMeta;
  lastUpdatedAt?: Date;
  subforms: Subform[];
  fields: FieldConfig[];
  variables: VariableConfig[];
  rules: Rule[];
  integrations: Integration[];
  settings: {
    saveMode: SaveMode;
    descriptionInApp: StringTemplate;
    searchSettings: {
      enabled: boolean;
      allowSearchingAllSubmissions: boolean;
      filteringEnabled: boolean;
      filteredFields: FieldId[];
      fields: FieldId[];
    };
  };
  theme: {
    id: ThemeId;
  };
};

export type Subform = {
  uid: SubformId;
  meta: {
    name: string;
  };
  emailConfiguration?: SubformEmailConfiguration;
};

export type SubformEmailConfiguration = {
  pinFieldsToAttach: FieldId[];
  content: StringTemplate;
  pdfContent: string;
  simpleMode: boolean;
  hideNoValues: boolean;
  horizontalOrientation: boolean;
};

export type FormScope = SubformId | Scope.ROOT;

export enum RuleActionKey {
  VISIBLE = "visible",
  HIDDEN = "hidden",
  VALUE = "value",
  REFERENCE = "reference",
  EMPTY = "empty",
}

export type RuleOperator = "AND" | "OR";
export type RuleConditionKey = "is" | "startsWith" | "endsWith" | "contains" | "greaterThan" | "lessThan" | "hasValue";
export type RuleConditionType = "FIELD" | "VARIABLE" | "FIELD_DATA_SOURCE" | "USER" | "EMPTY";

export type Rule = {
  id: RuleId;
  scope: FormScope;
  name?: string;
  operator: RuleOperator;
  conditions: RuleCondition[];
  actions: RuleAction[];
};

export type BaseRuleCondition = {
  key?: RuleConditionKey;
  type: RuleConditionType;
  value?: string | boolean;
};

export type FieldCondition = BaseRuleCondition & {
  fieldId: FieldId;
  type: "FIELD";
};

export type VariableCondition = BaseRuleCondition & {
  variableId: VariableId;
  type: "VARIABLE";
};

export type DataSourceFieldCondition = BaseRuleCondition & {
  fieldId: FieldId;
  type: "FIELD_DATA_SOURCE";
  fieldObjectKey: string;
};

type UserCondition = BaseRuleCondition & {
  type: "USER";
};

type EmptyCondition = BaseRuleCondition & {
  type: "EMPTY";
};

export type RuleCondition =
  | FieldCondition
  | VariableCondition
  | DataSourceFieldCondition
  | UserCondition
  | EmptyCondition;

type BaseRuleAction = {
  fieldId: FieldId;
  key: RuleActionKey;
};

export type MissingAction = {
  fieldId: FieldId;
  key?: undefined;
};

export type EmptyAction = {
  fieldId?: FieldId;
  key: RuleActionKey.EMPTY;
};

export type VisibleAction = BaseRuleAction & {
  key: RuleActionKey.VISIBLE | RuleActionKey.HIDDEN;
};

export type ValueAction = BaseRuleAction & {
  key: RuleActionKey.VALUE;
  value?: string | boolean;
};

export type ReferenceValueAction = BaseRuleAction & {
  key: RuleActionKey.REFERENCE;
  value?: FieldId | VariableId;
};

export type RuleAction = MissingAction | VisibleAction | ValueAction | ReferenceValueAction | EmptyAction;

export type SaveMode = "ALL" | "SAVE_AND_CLOSE_ONLY" | "NO_SAVE";

type FormVersionMeta = {
  created: Date;
  createdBy: string;
  lastUpdated: Date;
  lastUpdatedBy: string;
  status: "DRAFT" | "FINAL";
};

type State = FormVersion;

type Dispatch = SetStoreFunction<FormVersion>;

const StateContext = createContext<State>();
const DispatchContext = createContext<Dispatch>();

export const FormVersionProvider: ContextProviderComponent<FormVersion> = (props) => {
  const [formVersionStore, setFormVersionStore] = createStore(untrack(() => props.value));

  return (
    <StateContext.Provider value={formVersionStore}>
      <DispatchContext.Provider value={setFormVersionStore}>{props.children}</DispatchContext.Provider>
    </StateContext.Provider>
  );
};

export const useFormVersionState = () => initializedContext(StateContext, "useFormState", "FormVersionProvider");
export const useFormVersionDispatch = () =>
  initializedContext(DispatchContext, "useFormDispatch", "FormVersionProvider");
